import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, Pencil, Trash2, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import { changeLogsAPI } from '../api';
import { Card, Table, Pagination, PageHeader, Button, useSort } from '../components/UI';

const ACTION_META = {
  create:  { label: 'Create',  icon: Plus,      color: 'var(--accent-green)' },
  update:  { label: 'Update',  icon: Pencil,    color: 'var(--accent-blue)' },
  delete:  { label: 'Delete',  icon: Trash2,    color: 'var(--accent-red)' },
  restore: { label: 'Restore', icon: RotateCcw, color: 'var(--accent-orange)' },
};

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || { label: action || '—', icon: Pencil, color: 'var(--text-muted)' };
  const Icon = meta.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 9px',
      borderRadius: 20, background: `${meta.color}1A`, color: meta.color, border: `1px solid ${meta.color}44`, fontWeight: 500,
    }}>
      <Icon size={11} /> {meta.label}
    </span>
  );
}

// Hitung fields yang berubah dari before/after
function getDiffRows(row) {
  const before = row.before || row.metadata?.before || {};
  const after  = row.after  || row.metadata?.after  || {};
  const changed = row.changed_fields || row.metadata?.changed_fields;

  // Kalau ada changed_fields, pakai itu sebagai filter key
  const keys = changed?.length
    ? changed
    : [...new Set([...Object.keys(before), ...Object.keys(after)])];

  return keys.map(k => ({
    field: k,
    before: before[k],
    after:  after[k],
    changed: JSON.stringify(before[k]) !== JSON.stringify(after[k]),
  }));
}

function DiffSummary({ row }) {
  const rows = getDiffRows(row);
  if (!rows.length) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
  const changedFields = rows.filter(r => r.changed).map(r => r.field);
  if (!changedFields.length) changedFields.push(...rows.map(r => r.field));
  return (
    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
      {changedFields.length} field{changedFields.length > 1 ? 's' : ''}
      <span style={{ marginLeft: 6, color: 'var(--text-muted)', opacity: 0.7 }}>
        ({changedFields.slice(0, 4).join(', ')}{changedFields.length > 4 ? ', …' : ''})
      </span>
    </span>
  );
}

function DiffExpandedTable({ row }) {
  const rows = getDiffRows(row);
  if (!rows.length) return <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>Tidak ada data perubahan.</div>;

  const isCreate = row.action === 'create';
  const isDelete = row.action === 'delete';

  const fmt = (v) => {
    if (v === null || v === undefined) return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>null</span>;
    if (typeof v === 'boolean') return <span style={{ color: 'var(--accent-orange)' }}>{String(v)}</span>;
    const s = String(v);
    // sensor password/secret
    if (s === '[REDACTED]') return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>[REDACTED]</span>;
    return s;
  };

  return (
    <div style={{ padding: '4px 16px 16px 48px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', width: '22%' }}>Field</th>
            {!isCreate && <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--accent-red)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', width: '39%' }}>Before</th>}
            {!isDelete && <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--accent-green)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', width: '39%' }}>After</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ field, before, after, changed }) => (
            <tr key={field} style={{
              borderBottom: '1px solid var(--border)',
              background: changed && !isCreate && !isDelete ? 'rgba(59,130,246,0.04)' : 'transparent',
            }}>
              <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', color: changed ? 'var(--accent-blue)' : 'var(--text-secondary)', fontWeight: changed ? 600 : 400, fontSize: 11 }}>
                {field}
              </td>
              {!isCreate && (
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', fontSize: 11, wordBreak: 'break-all' }}>
                  {fmt(before)}
                </td>
              )}
              {!isDelete && (
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', fontSize: 11, wordBreak: 'break-all' }}>
                  {fmt(after)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const RESOURCE_TYPES = ['tenant', 'user', 'profile', 'nas', 'radius_server', 'admin_user'];
const ACTION_TYPES   = ['create', 'update', 'delete', 'restore'];

export default function ChangeLog() {
  const [logs, setLogs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(20);
  const [resourceType, setResourceType] = useState('');
  const [action, setAction]       = useState('');
  const [q, setQ]                 = useState('');
  const [loading, setLoading]     = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const { sort, onSortChange, sortParam } = useSort('created_at', 'desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (resourceType) params.resource_type = resourceType;
      if (action) params.action = action;
      if (q) params.q = q;
      if (sortParam) params.sort = sortParam;
      const res = await changeLogsAPI.list(params);
      setLogs(res.change_logs || []);
      setTotal(res.total ?? res.change_logs?.length ?? 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { /* non-critical */ }
    finally { setLoading(false); }
  }, [page, perPage, resourceType, action, q, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [resourceType, action, q]);

  // Collapse semua saat data berubah
  useEffect(() => { setExpandedIds(new Set()); }, [logs]);

  return (
    <div className="animate-in">
      <PageHeader title="Change Log" subtitle={`${total} riwayat perubahan data`} />

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari…" value={q} onChange={e => setQ(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 200 }} />
        <select value={resourceType} onChange={e => setResourceType(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Resource</option>
          {RESOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={action} onChange={e => setAction(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Action</option>
          {ACTION_TYPES.map(t => <option key={t} value={t}>{ACTION_META[t]?.label || t}</option>)}
        </select>
        <Button variant="ghost" onClick={load}><RefreshCw size={13} /> Refresh</Button>
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Belum ada change log</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ width: 32 }} />
                {[
                  { label: 'Time', key: 'created_at' },
                  { label: 'Action', key: 'action' },
                  { label: 'Resource', key: 'resource_type' },
                  { label: 'Name', key: null },
                  { label: 'By', key: null },
                  { label: 'Changes', key: null },
                ].map(h => {
                  const active = h.key && sort?.key === h.key;
                  return (
                    <th key={h.label} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: active ? 'var(--text-primary)' : 'var(--text-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      {h.key ? (
                        <button onClick={() => onSortChange(h.key)}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}>
                          {h.label}
                          <span style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>{active ? (sort.dir === 'desc' ? '▼' : '▲') : '⇅'}</span>
                        </button>
                      ) : h.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {logs.map((row, i) => {
                const id = row.id ?? i;
                const expanded = expandedIds.has(id);
                const hasDiff  = getDiffRows(row).length > 0;
                return (
                  <React.Fragment key={id}>
                    <tr
                      onClick={() => hasDiff && toggleExpand(id)}
                      style={{
                        borderBottom: expanded ? 'none' : '1px solid var(--border)',
                        cursor: hasDiff ? 'pointer' : 'default',
                        background: expanded ? 'var(--bg-card-hover)' : 'transparent',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                      onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Expand chevron */}
                      <td style={{ padding: '10px 0 10px 12px', width: 32 }}>
                        {hasDiff && (
                          expanded
                            ? <ChevronDown  size={14} style={{ color: 'var(--accent-blue)' }} />
                            : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                        )}
                      </td>

                      {/* Waktu */}
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {row.created_at ? new Date(row.created_at).toLocaleString('id-ID', { hour12: false }) : '—'}
                        </span>
                      </td>

                      {/* Action */}
                      <td style={{ padding: '10px 12px' }}>
                        <ActionBadge action={row.action} />
                      </td>

                      {/* Resource */}
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4 }}>{row.resource_type || '—'}</span>
                        {row.resource_id != null && (
                          <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>#{row.resource_id}</span>
                        )}
                      </td>

                      {/* Nama */}
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                          {row.resource_name || row.metadata?.target_username || row.metadata?.name || '—'}
                        </span>
                      </td>

                      {/* Oleh */}
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>
                          {row.admin_user_username || row.admin_user?.username || '—'}
                        </div>
                        {(row.admin_user_role || row.admin_user?.role) && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{row.admin_user_role || row.admin_user?.role}</div>
                        )}
                      </td>

                      {/* Perubahan (summary) */}
                      <td style={{ padding: '10px 12px' }}>
                        <DiffSummary row={row} />
                      </td>
                    </tr>

                    {/* Expanded diff */}
                    {expanded && (
                      <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-card-hover)' }}>
                        <td colSpan={7} style={{ padding: 0 }}>
                          <DiffExpandedTable row={row} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        <div style={{ padding: '0 16px' }}>
          <Pagination
            page={page} totalPages={totalPages} total={total}
            onChange={setPage} perPage={perPage}
            onPerPageChange={n => { setPerPage(n); setPage(1); }}
          />
        </div>
      </Card>
    </div>
  );
}